/**
 * This file started off from the @react-aria's `useComboBox` implementation
 * which can be found here
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/combobox/src/useComboBox.ts
 *
 * At the time of writing, @react-aria did not directly support select or
 * combobox with multiple selection. The reason being that there is no such
 * ARIA pattern and some interactions of a multiselect can't be translated to a
 * native <select> which is what `useSelect` tries to emulate.
 *
 * Some relevant discussions can be found here:
 * https://github.com/adobe/react-spectrum/issues/2140
 * https://github.com/adobe/react-spectrum/discussions/2218
 */

import { announce } from '@react-aria/live-announcer';
import { AriaButtonProps } from '@react-types/button';
import { AriaComboBoxProps } from '@react-types/combobox';
import { ariaHideOutside } from '@react-aria/overlays';
import { AriaListBoxOptions, getItemId, listData } from '@react-aria/listbox';
import {
  AriaLabelingProps,
  BaseEvent,
  DOMAttributes,
  KeyboardDelegate,
  PressEvent,
} from '@react-types/shared';
import { chain, isAppleDevice, mergeProps, useLabels } from '@react-aria/utils';
import {
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  RefObject,
  TouchEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getChildNodes, getItemCount } from '@react-stately/collections';
import {
  ListKeyboardDelegate,
  useSelectableCollection,
} from '@react-aria/selection';
import { MultipleSelection } from '@react-types/shared';
import { useMessageFormatter } from '@react-aria/i18n';
import { useMenuTrigger } from '@react-aria/menu';
import { useTextField } from '@react-aria/textfield';
import { MultiComboBoxState } from './useMultiComboBoxState';
import intlMessages from '../i18n';
import {
  AriaComboBoxPropsKeys,
  FocusEventsKeys,
  InputBaseKeys,
  KeyboardEventsKeys,
} from '../../utils/types';

// Omit all single-selection related props from SelectProps and add all
// multi-selection related props.
export type AriaMultiComboBoxOptions<T> = Pick<
  AriaComboBoxProps<T>,
  // even if this prop is not used directly in the components, it is needed for `useTextField`
  | 'errorMessage'
  | 'description'
  | FocusEventsKeys
  | AriaComboBoxPropsKeys
  | InputBaseKeys
  | KeyboardEventsKeys
> &
  AriaLabelingProps &
  MultipleSelection & {
    /** The ref for the input element. */
    inputRef: RefObject<HTMLInputElement>;

    /** The ref for the list box popover. */
    popoverRef: RefObject<Element>;

    /** The ref for the list box. */
    listBoxRef: RefObject<HTMLElement>;

    /** The ref for the list box popup trigger button.  */
    buttonRef: RefObject<Element>;

    /** An optional keyboard delegate implementation, to override the default. */
    keyboardDelegate?: KeyboardDelegate;
  };

export interface MultiComboBoxAria<T> {
  /** Props for the label element. */
  labelProps: DOMAttributes;

  /** Props for the combo box input element. */
  inputProps: InputHTMLAttributes<HTMLInputElement>;

  /** Props for the list box, to be passed to [useListBox](useListBox.html). */
  listBoxProps: AriaListBoxOptions<T>;

  /** Props for the optional trigger button, to be passed to [useButton](useButton.html). */
  buttonProps: AriaButtonProps;

  /** Props for the combo box description element, if any. */
  descriptionProps: DOMAttributes;

  /** Props for the combo box error message element, if any. */
  errorMessageProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a multi combo box component.
 * A multi combo box combines a text input with a listbox, allowing users to
 * filter a list of options to items matching a query and select one or more of them.
 * @param props - Props for the multi combo box.
 * @param state - State for the multiselect, as returned by `useMultiComboBoxState`.
 */
export function useMultiComboBox<T>(
  props: AriaMultiComboBoxOptions<T>,
  state: MultiComboBoxState<T>
): MultiComboBoxAria<T> {
  const {
    buttonRef,
    popoverRef,
    inputRef,
    listBoxRef,
    keyboardDelegate,
    // completionMode = 'suggest',
    shouldFocusWrap,
    isReadOnly,
    isDisabled,
  } = props;

  const formatMessage = useMessageFormatter(intlMessages);

  const { menuTriggerProps, menuProps } = useMenuTrigger<T>(
    {
      type: 'listbox',
      isDisabled: isDisabled || isReadOnly,
    },
    state,
    buttonRef
  );

  // Set listbox id so it can be used when calling getItemId later
  listData.set(state, { id: menuProps.id || '' });

  // By default, a KeyboardDelegate is provided which uses the DOM to query
  // layout information (e.g. for page up/page down). When virtualized, the
  // layout object will be passed in as a prop and override this.
  const delegate = useMemo(
    () =>
      keyboardDelegate ||
      new ListKeyboardDelegate(
        state.collection,
        state.disabledKeys,
        listBoxRef
      ),
    [keyboardDelegate, state.collection, state.disabledKeys, listBoxRef]
  );

  // Use useSelectableCollection to get the keyboard handlers to apply to the textfield
  const { collectionProps } = useSelectableCollection({
    selectionManager: state.selectionManager,
    keyboardDelegate: delegate,
    disallowTypeAhead: true,
    shouldFocusWrap,
    ref: inputRef,
    // In a searchable multiselect "select all" does not make that much sense:
    //  - what does it mean to select all from a filtered list, all visible or
    //  all including what's not visible?
    //  - cmd/ctrl + a is useful to select all the text in the input which is
    //  focused. Hijacking that seems agressive.
    //  - couldn't find a filterable multiselect in the wild which supports
    //  select all (assuming this is because of the UX implications)
    disallowSelectAll: true,
    // Prevent item scroll behavior from being applied here, should be handled
    // in the user's Popover + ListBox component
    isVirtualized: true,
  });

  // For textfield specific keydown operations
  const onKeyDown = (e: BaseEvent<KeyboardEvent<any>>) => {
    switch (e.key) {
      case 'Enter':
        if (state.isOpen) {
          e.preventDefault();
        }

        state.commit();
        break;
      case 'Escape':
        state.close();
        break;
      case 'ArrowDown':
        state.open('first', 'manual');
        break;
      case 'ArrowUp':
        state.open('last', 'manual');
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        state.selectionManager.setFocusedKey(null);
        break;
    }
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    // Ignore blur if focused moved into the popover.
    if (popoverRef.current?.contains(e.relatedTarget)) {
      return;
    }

    if (props.onBlur) {
      props.onBlur(e);
    }

    state.setFocused(false);
  };

  const onFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (state.isFocused) {
      return;
    }

    if (props.onFocus) {
      props.onFocus(e);
    }

    state.setFocused(true);
  };

  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField(
      {
        ...props,
        onChange: state.setInputValue,
        onKeyDown: (e) => {
          !isReadOnly &&
            chain(
              state.isOpen && e.key !== 'Escape' && collectionProps.onKeyDown,
              onKeyDown,
              props.onKeyDown
            )(e);
        },
        onBlur: onBlur as (e: FocusEvent<Element, Element>) => void,
        value: state.inputValue,
        onFocus: onFocus as (e: FocusEvent<Element, Element>) => void,
        autoComplete: 'off',
      },
      inputRef
    );

  // Press handlers for the ComboBox button
  const onPress = (e: PressEvent) => {
    if (e.pointerType === 'touch') {
      // Focus the input field in case it isn't focused yet
      inputRef.current?.focus();
      state.toggle(null, 'manual');
    }
  };

  const onPressStart = (e: PressEvent) => {
    if (e.pointerType !== 'touch') {
      inputRef.current?.focus();
      state.toggle(
        e.pointerType === 'keyboard' || e.pointerType === 'virtual'
          ? 'first'
          : null,
        'manual'
      );
    }
  };

  const triggerLabelProps = useLabels({
    id: menuTriggerProps.id,
    'aria-label': formatMessage('buttonLabel'),
    'aria-labelledby': props['aria-labelledby'] || labelProps.id,
  });

  const listBoxProps = useLabels({
    id: menuProps.id,
    'aria-label': formatMessage('listboxLabel'),
    'aria-labelledby': props['aria-labelledby'] || labelProps.id,
  });

  // If a touch happens on direct center of ComboBox input, might be virtual click from iPad so open ComboBox menu
  const lastEventTime = useRef(0);
  const onTouchEnd = (e: TouchEvent) => {
    if (isDisabled || isReadOnly) {
      return;
    }

    // Sometimes VoiceOver on iOS fires two touchend events in quick succession. Ignore the second one.
    if (e.timeStamp - lastEventTime.current < 500) {
      e.preventDefault();
      inputRef.current?.focus();
      return;
    }

    const rect = (e.target as Element).getBoundingClientRect();
    const touch = e.changedTouches[0];

    const centerX = Math.ceil(rect.left + 0.5 * rect.width);
    const centerY = Math.ceil(rect.top + 0.5 * rect.height);

    if (touch.clientX === centerX && touch.clientY === centerY) {
      e.preventDefault();
      inputRef.current?.focus();
      state.toggle(null, 'manual');

      lastEventTime.current = e.timeStamp;
    }
  };

  // VoiceOver has issues with announcing aria-activedescendant properly on change
  // (especially on iOS). We use a live region announcer to announce focus changes
  // manually. In addition, section titles are announced when navigating into a new section.
  const focusedItem =
    state.selectionManager.focusedKey != null && state.isOpen
      ? state.collection.getItem(state.selectionManager.focusedKey)
      : undefined;
  const sectionKey = focusedItem?.parentKey ?? null;
  const itemKey = state.selectionManager.focusedKey ?? null;
  const lastSection = useRef(sectionKey);
  const lastItem = useRef(itemKey);
  useEffect(() => {
    if (
      itemKey &&
      isAppleDevice() &&
      focusedItem != null &&
      itemKey !== lastItem.current
    ) {
      const isSelected = state.selectionManager.isSelected(itemKey);
      const section =
        sectionKey != null ? state.collection.getItem(sectionKey) : null;
      const sectionTitle =
        section?.['aria-label'] ||
        (typeof section?.rendered === 'string' ? section.rendered : '') ||
        '';

      const announcement = formatMessage('focusAnnouncement', {
        isGroupChange: section !== null && sectionKey !== lastSection.current,
        groupTitle: sectionTitle,
        groupCount: section
          ? [...getChildNodes(section, state.collection)].length
          : 0,
        optionText: focusedItem['aria-label'] || focusedItem.textValue || '',
        isSelected,
      });

      announce(announcement);
    }

    lastSection.current = sectionKey;
    lastItem.current = itemKey;
  });

  // Announce the number of available suggestions when it changes
  const optionCount = getItemCount(state.collection);
  const lastSize = useRef(optionCount);
  const lastOpen = useRef(state.isOpen);
  useEffect(() => {
    // Only announce the number of options available when the menu opens if there is no
    // focused item, otherwise screen readers will typically read e.g. "1 of 6".
    // The exception is VoiceOver since this isn't included in the message above.
    const didOpenWithoutFocusedItem =
      state.isOpen !== lastOpen.current &&
      (state.selectionManager.focusedKey == null || isAppleDevice());

    if (
      state.isOpen &&
      (didOpenWithoutFocusedItem || optionCount !== lastSize.current)
    ) {
      const announcement = formatMessage('countAnnouncement', {
        optionCount,
      });
      announce(announcement);
    }

    lastSize.current = optionCount;
    lastOpen.current = state.isOpen;
  });

  useEffect(() => {
    if (state.isOpen && inputRef.current && popoverRef.current) {
      return ariaHideOutside([inputRef.current, popoverRef.current]);
    }
  }, [state.isOpen, inputRef, popoverRef]);

  return {
    labelProps,
    buttonProps: {
      ...menuTriggerProps,
      ...triggerLabelProps,
      excludeFromTabOrder: true,
      onPress,
      onPressStart,
      isDisabled: isDisabled || isReadOnly,
    },
    inputProps: mergeProps(inputProps, {
      role: 'combobox',
      'aria-expanded': menuTriggerProps['aria-expanded'],
      'aria-controls': state.isOpen ? menuProps.id : undefined,
      // TODO: readd proper logic for completionMode = complete (aria-autocomplete: both)
      'aria-autocomplete': 'list',
      'aria-activedescendant': focusedItem
        ? getItemId(state, focusedItem.key)
        : undefined,
      onTouchEnd,
      // This disable's iOS's autocorrect suggestions, since the combo box provides its own suggestions.
      autoCorrect: 'off',
      // This disable's the macOS Safari spell check auto corrections.
      spellCheck: 'false',
    }),
    listBoxProps: mergeProps(menuProps, listBoxProps, {
      autoFocus: state.focusStrategy,
      shouldUseVirtualFocus: true,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: true,
    }),
    descriptionProps,
    errorMessageProps,
  };
}
